import { useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { Key } from "@phosphor-icons/react";
import { usePortalIdentity, canWrite, personName } from "../auth/usePortalIdentity";
import {
  chatMessagesKey,
  useChatMessages,
  useMarkChatRead,
  useSendChatMessage
} from "../data/portalQueries";
import {
  attachmentRefusal,
  chatAttachmentsKey,
  useAttachmentDownload,
  useChatAttachments,
  useComposeWithAttachments,
  type AttachmentUploadFailure
} from "../data/attachmentQueries";
import { useTenantRoster } from "../data/tenantRoster";
import { usePortalChannel } from "../realtime/usePortalChannel";
import { AttachmentList } from "../components/AttachmentList";
import { attributeActor, type ActorKind, type TenantRoster } from "../lib/actor";
import { draftKey, useSessionDraft } from "../lib/drafts";
import { BoxState } from "../ui/BoxState";
import { ClientHead } from "../ui/ClientHead";
import { Composer } from "../ui/Composer";
import { ThreadLog, type ThreadMessage } from "../ui/ThreadLog";
import { useViewOnly } from "../data/readSource";

/**
 * The one write path this slice proves end to end.
 *
 * The composer is disabled when the server says the tenant may not write, and the reason shown is
 * the server's state rather than a guess. Disabling it is a courtesy, not a control: the gateway
 * refuses the same write regardless, and the attack suite calls the RPC with the composer bypassed
 * entirely to prove exactly that.
 *
 * Every message says who sent it, in words. It did not: authorship was carried entirely by the
 * `is-own` class, which is a right margin and a background tint, so a screen reader received a list
 * of bodies and timestamps with no speaker in it, and a colleague in the same tenant was pixel for
 * pixel identical to Petrovic. design.md states the rule for the navigation rail and it is the same
 * rule here: a state is visible through shape, text weight and contrast, not colour alone.
 *
 * The author line is the request thread's line, not a second design: the same round avatar, the same
 * name in `strong` beside the same `time`. Two lists of authored entries in one portal should not
 * read as two different products.
 *
 * The name in that line is now the tenant's own name for the person wherever the sender is one of
 * their people. Saying it in words fixed the screen reader and left the harder half standing: the
 * word being said about a colleague was still "Petrovic". A tenant with two employees saw one of
 * them attributed to their supplier, in text, accessibly, and wrongly.
 */

/*
 * Rebuilt on 2026-09-24 (second build round) after the client sketch's Chatt, version 1: bubbles in
 * one box, the thread Petrovic's Konversation draws with the same `ThreadLog`, and the composer in
 * one row (attach, write, send) that rides above the phone's bottom bar. Everything above still
 * holds; what changed is the drawing:
 *
 * - the reader's own messages sit on the right, tinted, with their name kept for a screen reader and
 *   not drawn; Petrovic sits on the left with the brand face; a colleague sits on the reader's side
 *   with a face and no tint, and a sender whose account is gone sits in the middle. The sketch drew
 *   the colleague on Petrovic's side, and that is the one place this page does not follow it, for
 *   the reason `CHAT_CLASS` below gives;
 * - days are named between the bubbles ("I dag", "I går"), and a bubble carries only its time;
 * - the rule against secrets is the one line under the composer, a link to Hemligheter, instead of a
 *   paragraph under the heading and a second sentence in the composer (design.md section 3, rule 3);
 * - in Läsläge the composer is one locked line, where it used to be a disabled box whose placeholder
 *   and a notice under it said the same thing twice;
 * - the page opens at the newest message on a phone too, which `ThreadLog` does (task A, 2026-09-25).
 */
const MAX_LENGTH = 5000;

/** The newest 200 are read, `app/data/portalQueries.ts` `useChatMessages`. */
const CHAT_WINDOW = 200;

const dateTime = new Intl.DateTimeFormat("sv-SE", { dateStyle: "short", timeStyle: "short" });

/**
 * Where a message sits, given who wrote it.
 *
 * Position answers one question only: did the reader write this. `is-own` is the reader's own
 * message and `is-unknown` is centred, because a message whose `sender_user_id` is null belongs to
 * neither side and a layout that kept placing it on Petrovic's side would go on saying something the
 * label has just denied.
 *
 * A colleague sits on the reader's side, with `is-colleague`, and that is the one thing position is
 * allowed to say. The side answers which of the two parties wrote the message, and a conversation
 * between a client and their supplier has exactly two parties however many people stand behind each
 * one. Placing a colleague where Petrovic sits would be the same fault the labels above remove,
 * restated one level up: the actor is known, and the party is still claimed wrongly.
 *
 * Which person it was is carried by the name, in text and in the accessible name, because that is
 * the only signal that can distinguish four speakers and it is the one a screen reader gets.
 */
const CHAT_PLACEMENT: Record<ActorKind, Pick<ThreadMessage, "mine" | "placement">> = {
  unknown: { mine: false, placement: "unknown" },
  self: { mine: true },
  colleague: { mine: false, placement: "colleague" },
  petrovic: { mine: false }
};

/**
 * Who a message is shown as having come from.
 *
 * `chat_messages.sender_user_id` is `uuid null ... on delete set null`, so a message whose sender's
 * account was later deleted keeps its body and loses its author; that is the null case, and it is
 * `Okänd avsändare`.
 *
 * Every other sender that is not the reader used to be `Petrovic`, which turned the tenant's second
 * employee into the supplier. The roster decides that now, and the four answers live in
 * `app/lib/actor.ts` rather than here, because the request thread renders the same four and one
 * switch statement in two files is one rule in two places.
 */
function chatSender(
  senderUserId: string | null,
  currentUserId: string | null | undefined,
  ownName: string | null,
  roster: TenantRoster
): { name: string; initial: string; kind: ActorKind } {
  const actor = attributeActor(senderUserId, currentUserId, ownName, roster);

  return { name: actor.name, initial: actor.initial, kind: actor.kind };
}

/** Said to a screen reader before the name, so a side is never carried by position alone. */
const SIDE_WORD: Record<ActorKind, string> = {
  self: "Du",
  colleague: "Er kollega",
  petrovic: "",
  unknown: ""
};

export function ChatRoute() {
  const identity = usePortalIdentity();
  const tenantId = identity.data?.tenantId ?? null;
  const messages = useChatMessages(tenantId);
  const roster = useTenantRoster(tenantId);
  const attachments = useChatAttachments(tenantId);
  const send = useSendChatMessage();
  const compose = useComposeWithAttachments();
  const download = useAttachmentDownload();
  const markRead = useMarkChatRead();
  /*
    Kept in the tab's `sessionStorage` since 2026-09-22, so stepping over to another screen and back
    no longer empties the box. `app/lib/drafts.ts` says why the tab is the right lifetime. Cleared
    only once a message has actually landed, which is the same moment the idempotency key rotates.
  */
  const [draft, setDraft, clearDraft] = useSessionDraft(
    draftKey(identity.data?.userId, tenantId, "chat"),
    ""
  );
  const [files, setFiles] = useState<File[]>([]);
  const [sendFailures, setSendFailures] = useState<AttachmentUploadFailure[]>([]);
  const idempotencyKey = useRef(crypto.randomUUID());

  /*
    The attachment list is invalidated by the same event the message list is. An answer from Petrovic
    that carries a file arrives as one broadcast, and a log that refreshed the message without the
    paperclip under it would show half of what was sent.
  */
  // Not in "Visa som kund" (build round 41): the client's channel is the client's.
  const viewOnly = useViewOnly();
  usePortalChannel(tenantId && !viewOnly ? { kind: "tenant", id: tenantId, channel: "chat" } : null, {
    invalidate: [chatMessagesKey(tenantId ?? "none"), chatAttachmentsKey(tenantId ?? "none")]
  });

  /**
   * Marking the conversation read, once per message that has actually arrived.
   *
   * plan.md section 5.2 requires unread state per user, and until 2026-09-12 nothing in the product
   * called `public.mark_chat_read`. `tests/rpc-surface-reachability.test.ts` used it as its control
   * for exactly that reason, so the count in `public.v_unread_chat_count` could only ever go up.
   *
   * **The trigger is the newest message's id, and the ref is what makes it fire once.** A render, a
   * refetch and a realtime invalidation that returns the same rows all leave `newestMessageId`
   * unchanged, so they cost nothing. The ref is set *before* the call rather than in `onSuccess`,
   * which is what keeps a refused call from becoming a loop: a rate-limited or offline attempt is
   * not retried by this effect at all, and the repair is the next message arriving or the next visit
   * to the page, because the ref is per mount.
   *
   * `private.consume_rate_limit('read_position', ...)` allows 60 an hour per person and that bucket
   * belongs to this act alone. One call per distinct message means the ceiling is only reachable by a
   * conversation of sixty messages in an hour, which is a thread nobody has, and the cost of reaching
   * it would be an unmarked position rather than a broken screen.
   *
   * Nothing is shown when it fails. The client did not ask for this and cannot act on the answer;
   * `mutate` swallows the rejection and the position is simply not moved. A refusal a reader can do
   * nothing about is noise, and the two it can meet are `PORTAL_RATE_LIMITED` and `PORTAL_OFFLINE`.
   *
   * It is deliberately not gated on `canWrite`. `mark_chat_read` checks
   * `public.current_tenant_ids()` rather than the write list, precisely so that a client in the
   * read-only window can still clear a count they are still shown.
   */
  const rowsForRead = messages.data ?? [];
  const newestMessageId = rowsForRead.length > 0 ? rowsForRead[rowsForRead.length - 1].id : null;
  const markedThrough = useRef<string | null>(null);
  const markChatRead = markRead.mutate;

  useEffect(() => {
    if (!tenantId || !newestMessageId || markedThrough.current === newestMessageId) {
      return;
    }

    markedThrough.current = newestMessageId;
    markChatRead({ tenantId, lastReadMessageId: newestMessageId });
  }, [tenantId, newestMessageId, markChatRead]);

  /*
   * Following the newest message, and only for somebody who was already reading it, is `ThreadLog`'s
   * since 2026-09-24, with the same 160 px threshold this page used. It also opens the thread at its
   * newest message on a phone, where the page and not the log is what scrolls.
   */

  /**
   * The roster has to have arrived before a single message is drawn.
   *
   * `isLoading` is not the test. A query that is disabled because there is no tenant yet is pending
   * with nothing in flight, so `isLoading` is false while `data` is still undefined, and gating on
   * it would let the log render with an empty roster and call every colleague Petrovic for one
   * frame. The presence of the resolved map is the test, and the tenant check keeps the no-tenant
   * case falling through to its own refusal below rather than spinning forever.
   */
  const rosterPending = Boolean(tenantId) && !roster.isError && roster.data === undefined;

  if (identity.isLoading || messages.isLoading || rosterPending) {
    return (
      <ChatFrame>
        <BoxState kind="loading" title="Hämtar konversationen" />
      </ChatFrame>
    );
  }

  if (!tenantId) {
    return (
      <ChatFrame>
        <BoxState
          kind="denied"
          title="Ingen aktiv åtkomst"
          body="Ditt konto är inte kopplat till något kundkonto just nu."
        />
      </ChatFrame>
    );
  }

  if (messages.isError) {
    return (
      <ChatFrame>
        <BoxState
          kind="error"
          title="Meddelandena kunde inte hämtas"
          body="Ladda om sidan. Står felet kvar, hör av er till Petrovic Systems."
          onRetry={() => void messages.refetch()}
        />
      </ChatFrame>
    );
  }

  // A failed roster read is shown as a failure and not worked around. The conversation is readable
  // and unlabelled, and rendering it that way would name every colleague Petrovic, which is a
  // confident false statement about people the client employs.
  if (roster.isError || !roster.data) {
    return (
      <ChatFrame>
        <BoxState
          kind="error"
          title="Vi kan inte visa vem som skrivit vad"
          body="Namnen på personerna i er organisation kunde inte hämtas. Konversationen visas därför inte just nu."
          onRetry={() => void roster.refetch()}
        />
      </ChatFrame>
    );
  }

  const writable = canWrite(identity.data);
  const rows = messages.data ?? [];
  const names = roster.data;
  const filesByMessage = attachments.data;
  const sending = send.isPending || compose.isPending;
  // A file the browser already knows will be refused must not be sent. The composer says why beside
  // each one; this is only what stops the form.
  const refusedFile = files.some((file) => attachmentRefusal(file) !== null);

  // Resolved the way the request thread resolves it, so a profile with a first name and no display
  // name is still a name here, and a whitespace name is treated as no name rather than as an avatar
  // with nothing in it.
  const person = personName(identity);
  const ownName = person.kind === "named" ? person.display : null;

  /**
   * Text, files, or both, and one call decides which.
   *
   * A message with no file goes straight to `public.send_chat_message`, as it always has. A message
   * with a file goes to `/api/files/compose`, which writes the message and admits its files in one
   * transaction, so a screenshot on its own is a message. Until 2026-09-13 the message went first and
   * the files after it, and the gateway refused a message with no text, so a screenshot could not be
   * sent without inventing a sentence to go with it.
   *
   * A refusal before anything was written keeps the draft, the files and the idempotency key, so a
   * retry is the same send. A file whose bytes fail after the message exists is reported and never
   * rolled back: the message is in the thread, and the file stays selected so a second send carries
   * only what did not arrive.
   */
  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const body = draft.trim();
    if ((!body && files.length === 0) || !tenantId) {
      return;
    }

    if (files.length === 0) {
      try {
        await send.mutateAsync({
          tenantId,
          body,
          idempotencyKey: idempotencyKey.current
        });
        clearDraft();
        // A new key only after a message actually landed, so a retry of a failed send cannot double-post.
        idempotencyKey.current = crypto.randomUUID();
        setSendFailures([]);
      } catch {
        // The failure is rendered from the mutation's own error below.
      }
      return;
    }

    try {
      const outcome = await compose.mutateAsync({
        tenantId,
        parent: { kind: "chat_message", body: body || null, idempotencyKey: idempotencyKey.current },
        files
      });

      clearDraft();
      idempotencyKey.current = crypto.randomUUID();
      setFiles(outcome.failed.map((entry) => entry.file));
      setSendFailures(outcome.failed);
    } catch {
      // Nothing was written. Rendered from the mutation's own error below.
    }
  }

  const threadMessages: ThreadMessage[] = rows.map((message) => {
    const sender = chatSender(message.sender_user_id, identity.data?.userId, ownName, names);
    const sentAt = dateTime.format(new Date(message.sent_at));

    return {
      id: message.id,
      ...CHAT_PLACEMENT[sender.kind],
      sideWord: SIDE_WORD[sender.kind],
      author: sender.name,
      authorHidden: sender.kind === "self",
      initials: sender.initial,
      petrovic: sender.kind === "petrovic",
      sentAt: message.sent_at,
      body: message.body,
      // A message that is only a file has no text, and the file under it is the whole message.
      missingBody: "",
      attachments: (
        <AttachmentList
          tenantId={tenantId}
          attachments={filesByMessage?.get(message.id) ?? []}
          label={`Bilagor från ${sender.name}, ${sentAt}`}
          download={download}
        />
      ),
      testId: "chat-message"
    };
  });

  return (
    <div className="k-page is-chat">
      <ClientHead title="Chatt med Petrovic Systems" titleId="chat-title" />

      {/*
        A failed attachment read is said once, above the thread, and never allowed to hide the
        conversation. The messages are a separate query and they are intact; what is missing is the
        list of files hanging off them.
      */}
      {attachments.isError ? (
        <section className="panel">
          <BoxState
            kind="error"
            compact
            title="Bilagorna kunde inte hämtas"
            body="Konversationen visas, men filer som hör till meddelandena syns inte just nu."
            onRetry={() => void attachments.refetch()}
          />
        </section>
      ) : null}

      <section className="panel thread-panel k-chat" aria-labelledby="chat-title" data-testid="chat-panel">
        {rows.length >= CHAT_WINDOW ? (
          <p className="line-hint thread-cap" data-testid="chat-cap">
            De {CHAT_WINDOW} senaste visas. Äldre meddelanden finns kvar men syns inte här.
          </p>
        ) : null}

        <ThreadLog
          label="Er konversation med Petrovic Systems"
          live
          messages={threadMessages}
          empty={
            <BoxState
              kind="empty"
              title="Inga meddelanden ännu"
              body="Skriv det första meddelandet så svarar Petrovic Systems här."
            />
          }
        />

        {/*
          plan.md section 5.2: "Secrets are prohibited in chat. The composer points users to Send a
          secret when appropriate." The line sits under the composer, where the sentence is needed:
          at the moment somebody is about to type the thing.

          Enter sends here on a computer and starts a new line on a phone, since 2026-10-07 (build
          round 16). `Composer` says why, and why only the chat.
        */}
        <Composer
          id="chat-body"
          label="Nytt meddelande"
          value={draft}
          onChange={setDraft}
          onSubmit={(event) => void onSubmit(event)}
          placeholder="Skriv till Petrovic Systems"
          maxLength={MAX_LENGTH}
          pending={sending}
          canSubmit={(draft.trim().length > 0 || files.length > 0) && !refusedFile}
          locked={!writable}
          enterSends
          testId="chat-composer"
          attachments={{ inputId: "chat-attachment", files, onChange: setFiles, sendFailures }}
          foot={
            <Link className="k-secret-link" to="/secrets" data-testid="composer-secret">
              <Key size={16} aria-hidden="true" />
              Lösenord eller nyckel? Skicka en hemlighet
            </Link>
          }
          errors={
            <>
              {send.isError ? (
                <p className="form-error" role="alert" data-testid="send-error">
                  {send.error.message}
                </p>
              ) : null}
              {/*
                A refusal from the call that writes the message and admits its files. It is one
                transaction, so nothing was written and the sentence can say so plainly. A file that
                failed after the message landed is said beside that file instead.
              */}
              {compose.isError ? (
                <p className="form-error" role="alert" data-testid="compose-error">
                  {compose.error.message}
                </p>
              ) : null}
            </>
          }
        />
      </section>
    </div>
  );
}

/** The page's frame for its loading, refused and failed states: the heading, and the state in a box. */
function ChatFrame({ children }: { children: ReactNode }) {
  return (
    <div className="k-page is-chat">
      <ClientHead title="Chatt med Petrovic Systems" titleId="chat-title" />
      <section className="panel">{children}</section>
    </div>
  );
}
